// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as yup from "yup";
import {
  ALLOWED_URL_PROTOCOLS,
  GITHUB_DESCRIPTION_MAX,
  GITHUB_REPO_NAME_MAX,
  GITHUB_TOPIC_MAX_COUNT,
  GITHUB_TOPIC_MAX_LENGTH
} from "@config/config";

export const isIncludedRole = (a: string[], b: string[]): boolean => {
  return [...getCrossItems(a, b), ...getCrossItems(b, a)].length > 0;
};

function getCrossItems<Role>(a: Role[], b: Role[]): Role[] {
  return a.filter((element) => {
    return b.includes(element);
  });
}

export const formatDateTime = (dateTimeStr: string) => {
  const utcDate = new Date(dateTimeStr + " UTC");
  const day = String(utcDate.getDate()).padStart(2, "0");
  const month = String(utcDate.getMonth() + 1).padStart(2, "0");
  const year = utcDate.getFullYear();
  const hours = String(utcDate.getHours()).padStart(2, "0");
  const minutes = String(utcDate.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}, ${hours}:${minutes}`;
};

// Regex patterns
export const TOPIC_PATTERN = /^[a-z0-9-]+$/;
export const REPO_NAME_PATTERN = /^[A-Za-z0-9._-]+$/;
export const REPO_NAME_SINGLE_CHAR_PATTERN = /[A-Za-z0-9._-]/;
export const URL_SCHEMA_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//;
export const DATA_URL_SCHEME_PATTERN = /^\s*(javascript|data):/i;

// Sanitization functions
export const sanitizeDescription = (description: string): string =>
  description
    .replace(/[\r\n]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

export const sanitizeTopics = (rawTopicsString: string[] | string): string => {
  const arr = Array.isArray(rawTopicsString) ? rawTopicsString : rawTopicsString.split(",");
  const cleaned = arr
    .flatMap((v) => v.split(","))
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean);
  const unique = Array.from(new Set(cleaned));
  return unique.join(",");
};

export const sanitizeEmails = (emails: string): string => {
  return emails
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean)
    .join(",");
};

export const sanitizeRepoName = (name: string): string => {
  return name.trim();
};

// Validation schemas
export const emailListValidation = yup
  .string()
  .trim()
  .required("At least one email is required.")
  .test(
    "valid-emails",
    "All emails must be valid.",
    (value) =>
      !value ||
      value
        .split(",")
        .map((e) => e.trim())
        .every((email) => yup.string().email().isValidSync(email))
  );

export const repoNameValidation = yup
  .string()
  .trim()
  .required("Repository name is required.")
  // Normalize once for all subsequent tests
  .transform((v) => (typeof v === "string" ? v.trim() : ""))
  // Length constraint (GitHub limit = 100)
  .test(
    "length-max",
    `Repository name must be at most ${GITHUB_REPO_NAME_MAX} characters.`,
    (value) => (value || "").length <= GITHUB_REPO_NAME_MAX
  )
  // Reserved names
  .test("not-reserved", 'Repository name cannot be "." or "..".', (value) => {
    const v = (value || "").trim();
    return v !== "." && v !== "..";
  })
  // Suffix constraint
  .test(
    "no-dot-git",
    'Repository name must not end with ".git".',
    (value) => !/(?:^|.)\.git$/i.test((value || "").trim())
  )
  // Allowed character set with targeted message for *first* offending character
  .test("allowed-chars", "Invalid repository name.", function (value) {
    const { createError, path } = this as any;
    const v = (value || "").trim();

    if (!v) return true; // handled by required already

    if (!REPO_NAME_PATTERN.test(v)) {
      for (let i = 0; i < v.length; i++) {
        const ch = v[i];
        if (!REPO_NAME_SINGLE_CHAR_PATTERN.test(ch)) {
          return createError({
            path,
            message: `Invalid character at position ${
              i + 1
            }: "${ch}". Use only letters, numbers, hyphens (-), underscores (_), and periods (.).`,
          });
        }
      }
      // Fallback (shouldn't reach here)
      return createError({
        path,
        message:
          "Invalid repository name. Use only letters, numbers, hyphens (-), underscores (_), and periods (.).",
      });
    }
    return true;
  });

export const descriptionValidation = yup
  .string()
  .trim()
  .required("Description is required.")
  .max(
    GITHUB_DESCRIPTION_MAX,
    `Description cannot exceed ${GITHUB_DESCRIPTION_MAX} characters.`
  );


export const topicsValidation = yup
  .string()
  .trim()
  .required("Topics are required.")
  // Normalize once for all subsequent tests
  .transform((v) => (typeof v === "string" ? v : ""))
  // Count constraint
  .test(
    "count-max",
    `Add no more than ${GITHUB_TOPIC_MAX_COUNT} topics.`,
    (value) => {
      const arr = (value || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      return arr.length <= GITHUB_TOPIC_MAX_COUNT;
    }
  )
  // Duplicate detection (case-insensitive)
  .test(
    "no-duplicates",
    "Topics must be unique (case-insensitive).",
    (value) => {
      const arr = (value || "")
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      return arr.length === new Set(arr).size;
    }
  )
  // Per-topic validation with targeted message for the *first* offender
  .test("validate-each", "Invalid topics.", function (value) {
    const { createError, path } = this as any;
    const raw = (value || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    for (let i = 0; i < raw.length; i++) {
      const shown = raw[i];
      const t = shown.toLowerCase();

      if (t.length > GITHUB_TOPIC_MAX_LENGTH) {
        return createError({
          path,
          message: `Topic #${
            i + 1
          } ("${shown}") is too long (max ${GITHUB_TOPIC_MAX_LENGTH}).`,
        });
      }

      if (!TOPIC_PATTERN.test(t)) {
        return createError({
          path,
          message: `Topic #${
            i + 1
          } ("${shown}") is invalid. Use only lowercase letters, numbers, and hyphens.`,
        });
      }
    }
    return true;
  });

export const sanitizeUrl = (input: string | undefined | null): string => {
  if (!input) return "";
  // trim and strip control characters
  let cleaned = String(input).trim().replace(/[\u0000-\u001F\u007F]+/g, "");
  if (!cleaned) return "";

  // Reject javascript: and data: schemes explicitly
  if (DATA_URL_SCHEME_PATTERN.test(cleaned)) return "";

  // If missing a scheme, default to https:// to ensure parseable URL
  if (!URL_SCHEMA_PATTERN.test(cleaned)) {
    cleaned = "https://" + cleaned;
  }

  try {
    const u = new URL(cleaned);
    if (!ALLOWED_URL_PROTOCOLS.includes(u.protocol)) return "";
    return u.toString();
  } catch {
    return "";
  }
};

export const urlValidation = yup
  .string()
  .trim()
  .transform((v) => (v === "" ? undefined : v))
  .test(
    "has-protocol-allowlist",
    "Website URL must start with http:// or https://",
    (value) => {
      if (!value) return true;
      try {
        // If user omitted scheme, sanitize rules prefer https (but validation should fail if invalid).
        const toCheck = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value) ? value : `https://${value}`;
        const u = new URL(toCheck);
        return ALLOWED_URL_PROTOCOLS.includes(u.protocol);
      } catch {
        return false;
      }
    }
  )
  .url("Website URL must be a valid URL.")
  .notRequired();
